/**
 * Coup de pouce — Attendu 1061 « identifier la structure d'un motif évolutif
 * en repérant une régularité ». UNE page en QUATRE TEMPS (pas à pas natif,
 * cf. le helper svg ci-dessous), partagée par les trois generator_keys.
 * Phrases validées en texte nu par le prof le 2026-09-09, codées mot pour mot.
 *
 * L'image que l'élève a déjà : la frise de carrés en allumettes (638, 1060 :
 * 4 ; 7 ; 10). Standard de page : le dessin (la première allumette en bleu,
 * les trois de chaque carré en orange), un calcul par étape aligné sous le
 * dessin (« Étape 1 : 1 + 1 × 3 », « Étape 2 : 1 + 2 × 3 », « Étape 3 : 1 +
 * 3 × 3 » : le bleu est le 1, l'orange est le n × 3, et seul le numéro de
 * l'étape change d'une ligne à l'autre), le calcul en gros « Étape 10 : 1 +
 * 10 × 3 » SANS sa valeur (la valeur, c'est 1060), puis UNE ligne du geste :
 * « Étape 10 : 10 carrés, donc 10 groupes de 3. » Elle écrit ce que la figure
 * ne montre pas, et c'est la collision avec la fiche de 1060 qui dit
 * « 9 flèches ».
 *
 * SACRIFIÉ : le couple (f = 1, k = 3), exclu de toutes les figures du gen
 * (cdp1061.self.test.js le vérifie sur l'image rendue). Largeur bornée à
 * 340 px, tailles de 1048.
 */

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';

/**
 * PAS À PAS natif (data-etapes, convention de CoupDePouceSheet, modèle 621) :
 * l'élève avance avec la flèche, l'affichage est CUMULATIF. UNE SEULE frise
 * qui grandit, et à chaque temps ce sont les TROIS allumettes du carré suivant
 * qui se posent (prof, 2026-09-10 : « on voit les nouveaux se mettre au fur et
 * à mesure »). C'est ce que la page fixe ne pouvait pas montrer : le même
 * paquet de 3 revient, et un carré coûte 3 allumettes et non 4 puisqu'il
 * emprunte son côté gauche au précédent. L'allumette bleue est HORS des temps,
 * toujours visible : celui qui n'avance pas voit un carré entier et son
 * calcul, une fiche juste.
 * Ni SMIL ni keyframes : dans la feuille, une figure à étapes est toujours
 * pilotée à la main (le bouton « ▶ Animation » demande du <animate>), et hors
 * de la feuille tout s'affiche d'un coup, ce qui est la page complète.
 */
const svg = (aria, hauteur, corps) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 ${hauteur}" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     data-etapes="4" aria-label="${aria}">
  ${corps}
</svg>`.trim();

// ─── 1. La frise qui grandit : l'allumette de départ en bleu, 3 par carré en orange ──

const S = 56;
const X0 = 116;
const Y_BAS = 84;
const allumette = (x1, y1, x2, y2, couleur) =>
    `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${couleur}" stroke-width="5" stroke-linecap="round"/>`;
/** Le paquet du carré n : le haut, le bas, le côté droit. Le côté gauche est déjà posé. */
const paquet = (n) => {
    const x = X0 + (n - 1) * S;
    return allumette(x, Y_BAS - S, x + S, Y_BAS - S, ORANGE)
        + allumette(x, Y_BAS, x + S, Y_BAS, ORANGE)
        + allumette(x + S, Y_BAS, x + S, Y_BAS - S, ORANGE);
};

// ─── 2. Un temps par carré : le paquet de 3 et la ligne de calcul ─────────
// Les trois calculs partent du même x : les chiffres ont la même largeur, le
// numéro de l'étape se lit donc en colonne (prof, 2026-09-10 : « c'est mieux
// de décrire toutes les étapes »).

const X_CALCULS = 124;
const TEMPS = [1, 2, 3].map((n) => `<g data-etape="${n}">
    ${paquet(n)}
    <text x="${X_CALCULS}" y="${116 + (n - 1) * 22}" font-size="15" font-weight="bold" fill="${ENCRE}">Étape ${n} : <tspan fill="${BLEU}">1</tspan> + <tspan fill="${ORANGE}">${n} × 3</tspan></text>
  </g>`).join('\n  ');

const PAGE = svg(
    'Une frise de carrés en allumettes qui grandit. Une première allumette bleue est posée, puis chaque carré ajoute trois allumettes orange : son côté gauche est déjà là. Étape 1, un carré : 1 plus 1 fois 3. Étape 2, deux carrés : 1 plus 2 fois 3. Étape 3, trois carrés : 1 plus 3 fois 3. Le 1 est en bleu, le reste en orange. Étape 10 : 1 plus 10 fois 3. Étape 10 : 10 carrés, donc 10 groupes de 3.',
    252,
    `<!-- toujours visible : l'allumette de départ, ce qui ne change pas -->
  ${allumette(X0, Y_BAS, X0, Y_BAS - S, BLEU)}

  <!-- temps 1 à 3 : un carré par temps, ses trois allumettes et son calcul -->
  ${TEMPS}

  <!-- temps 4 : le calcul en gros (sans sa valeur) et le geste -->
  <g data-etape="4">
    <text x="200" y="208" font-size="18" font-weight="bold" text-anchor="middle" fill="${ENCRE}">Étape 10 : <tspan fill="${BLEU}">1</tspan> + <tspan fill="${ORANGE}">10 × 3</tspan></text>
    <text x="200" y="238" font-size="15" text-anchor="middle" fill="${ENCRE}">Étape 10 : 10 carrés, donc 10 groupes de 3.</text>
  </g>`,
);

const AIDE = { pages: [{ titre: 'Ce qui ne change pas, ce qui grandit', svg: PAGE }] };

const CP_1061 = {
    flash_texte_trou_saisie: AIDE,
    fixe_qcm_unique: AIDE,
    fixe_tableau_vf: AIDE,
};

export default CP_1061;
